import firebase from '../config/firebase.config';
import moment from 'moment-timezone';

const database = firebase.firestore();
const { FieldValue, Timestamp } = firebase.firestore;

/** Pad number to 2 digits */
const pad2 = (n) => String(n ?? 0).padStart(2, '0');

const DEFAULT_TIMEZONE = 'Asia/Kolkata';
const REGULARIZATION_REQUESTS_COLLECTION = 'regularizationRequests';
const MEMBER_DATABASE_COLLECTION = 'memberDatabase';
const REGULARIZATION_ASSIGNEES_SUBCOLLECTION = 'regularizationAssignees';

/**
 * Read start/end from Firestore: may be a Timestamp instance or a plain serialized map
 * `{ seconds, nanoseconds }` (no `.toDate()` in the client).
 */
export function toDateFromFirestoreTimestampField(value) {
  if (value == null) return null;
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const sec = value._seconds ?? value.seconds;
  const nanos = value._nanoseconds ?? value.nanoseconds ?? 0;
  if (sec != null) {
    const s = Number(sec);
    const n = Number(nanos) || 0;
    return new Date(s * 1000 + Math.floor(n / 1000000));
  }
  return null;
}

export function toFirestoreTimestampField(value) {
  const d = toDateFromFirestoreTimestampField(value);
  if (!d) return null;
  return Timestamp.fromDate(d);
}

/**
 * Build Firestore Timestamp for start time in UTC from date string + hours/minutes in the given timezone.
 * @param {string} dateStr - "YYYY-MM-DD"
 * @param {number} startHours - hour in selected timezone
 * @param {number} startMinutes - minute in selected timezone
 * @param {string} [timezone] - IANA timezone (e.g. "America/New_York"). If omitted, treats as local.
 */
function buildStartTimeUTC(dateStr, startHours, startMinutes, timezone) {
  const str = `${dateStr} ${pad2(startHours)}:${pad2(startMinutes)}:00`;
  const m = timezone
    ? moment.tz(str, 'YYYY-MM-DD HH:mm:ss', timezone)
    : moment(str, 'YYYY-MM-DD HH:mm:ss');
  return firebase.firestore.Timestamp.fromDate(m.toDate());
}

/**
 * Build Firestore Timestamp for end time in UTC from date string + endTime map in the given timezone.
 */
function buildEndTimeUTC(dateStr, endTime, timezone) {
  const endHours = endTime?.endHours ?? 0;
  const endMinutes = endTime?.endMinutes ?? 0;
  const str = `${dateStr} ${pad2(endHours)}:${pad2(endMinutes)}:00`;
  const m = timezone
    ? moment.tz(str, 'YYYY-MM-DD HH:mm:ss', timezone)
    : moment(str, 'YYYY-MM-DD HH:mm:ss');
  return firebase.firestore.Timestamp.fromDate(m.toDate());
}

const getRegularizationRequestsRef = (companyId) =>
  database
    .collection('companyDatabase')
    .doc(companyId)
    .collection(REGULARIZATION_REQUESTS_COLLECTION);

const getRegularizationRequestRef = (companyId, requestId) =>
  getRegularizationRequestsRef(companyId).doc(requestId);

const getAssigneeQueueRef = (companyId, assigneeId) =>
  database
    .collection(MEMBER_DATABASE_COLLECTION)
    .doc(companyId)
    .collection(REGULARIZATION_ASSIGNEES_SUBCOLLECTION)
    .doc(assigneeId);

const removeUndefinedDeep = (value) => {
  // Firestore Timestamp has enumerable seconds/nanoseconds; Object.entries would
  // strip the prototype and write a plain map to Firestore instead of timestamp type.
  if (value instanceof Timestamp) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(removeUndefinedDeep);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      if (typeof val === 'undefined') return acc;
      acc[key] = removeUndefinedDeep(val);
      return acc;
    }, {});
  }

  return value;
};

const normalizeRequestDocForWrite = (payload, now, timezone) => {
  const startTime = buildStartTimeUTC(
    payload.startTime,
    payload.startHours,
    payload.startMinutes,
    timezone
  );
  const endTime = buildEndTimeUTC(payload.startTime, payload.endTime, timezone);

  const {
    screenshots: _screenshots,
    startHours: _startHours,
    startMinutes: _startMinutes,
    endTime: _endTime,
    idleMinutes: _idleMinutes,
    id: _id,
    ...rest
  } = payload;

  return removeUndefinedDeep({
    ...rest,
    createdAt: now,
    updatedAt: now,
    startTime,
    endTime,
  });
};

/**
 * Regularization Service
 * Handles all database operations for regularization requests
 */
const regularizationService = {
  /**
   * Get all regularization requests for a user
   * @param {string} userId - User ID
   * @param {string} companyId - Company ID
   * @returns {Promise<Array>} Array of regularization requests
   */
  async getUserRequests(userId, companyId) {
    try {
      const snapshot = await getRegularizationRequestsRef(companyId)
        .where('userId', '==', userId)
        .orderBy('requestDate', 'desc')
        .get();

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching user regularization requests:', error);
      throw error;
    }
  },

  /**
   * Get all regularization requests assigned to a manager
   * @param {string} managerId - Manager ID
   * @param {string} companyId - Company ID
   * @returns {Promise<Array>} Array of regularization requests
   */
  async getManagerRequests(managerId, companyId) {
    try {
      const snapshot = await getRegularizationRequestsRef(companyId)
        .where('assignee', '==', managerId)
        .orderBy('requestDate', 'desc')
        .get();

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching manager regularization requests:', error);
      throw error;
    }
  },

  /**
   * Create a new regularization request
   * @param {Object} requestData - Request data
   * @returns {Promise<Object>} Created request with ID
   */
  async createRequest(requestData) {
    try {
      const requestRef = getRegularizationRequestsRef(
        requestData.companyID
      ).doc();
      const assigneeQueueRef = getAssigneeQueueRef(
        requestData.companyID,
        requestData.assignee
      );
      const now = Timestamp.now();
      const tz = requestData.timezone || DEFAULT_TIMEZONE;
      const doc = normalizeRequestDocForWrite(requestData, now, tz);

      await database.runTransaction(async (transaction) => {
        await transaction.set(requestRef, doc);
        await transaction.set(
          assigneeQueueRef,
          {
            regularizationRequests: FieldValue.arrayUnion(requestRef.id),
            updatedAt: now,
          },
          { merge: true }
        );
      });

      return { ...doc, id: requestRef.id };
    } catch (error) {
      console.error('Error creating regularization request:', error);
      throw error;
    }
  },

  /**
   * Update an existing regularization request
   * @param {string} requestId - Request ID
   * @param {Object} updateData - Data to update
   * @param {string} companyId - Company ID
   * @param {string} oldAssignee - Previous assignee ID
   * @param {string} newAssignee - New assignee ID
   * @returns {Promise<void>}
   */
  async updateRequest(
    requestId,
    updateData,
    companyId,
    oldAssignee,
    newAssignee
  ) {
    try {
      const requestRef = getRegularizationRequestRef(companyId, requestId);
      const updatedAt = Timestamp.now();
      const {
        screenshots: _screenshots,
        startHours: _startHours,
        startMinutes: _startMinutes,
        endTime: _endTime,
        idleMinutes: _idleMinutes,
        id: _id,
        ...rest
      } = updateData;
      const normalizedUpdate = removeUndefinedDeep({ ...rest, updatedAt });
      const updateTz = updateData.timezone || DEFAULT_TIMEZONE;
      if (
        updateData.startTime != null &&
        updateData.startHours != null &&
        updateData.startMinutes != null
      ) {
        normalizedUpdate.startTime = buildStartTimeUTC(
          updateData.startTime,
          updateData.startHours,
          updateData.startMinutes,
          updateTz
        );
      }
      if (updateData.startTime != null && updateData.endTime != null) {
        normalizedUpdate.endTime = buildEndTimeUTC(
          updateData.startTime,
          updateData.endTime,
          updateTz
        );
      }

      if (oldAssignee !== newAssignee) {
        await database.runTransaction(async (transaction) => {
          const oldAssigneeQueueRef = getAssigneeQueueRef(
            companyId,
            oldAssignee
          );
          const newAssigneeQueueRef = getAssigneeQueueRef(
            companyId,
            newAssignee
          );

          await transaction.update(requestRef, normalizedUpdate);
          await transaction.set(
            oldAssigneeQueueRef,
            {
              regularizationRequests: FieldValue.arrayRemove(requestId),
              updatedAt,
            },
            { merge: true }
          );
          await transaction.set(
            newAssigneeQueueRef,
            {
              regularizationRequests: FieldValue.arrayUnion(requestId),
              updatedAt,
            },
            { merge: true }
          );
        });
      } else {
        await requestRef.update(normalizedUpdate);
      }
    } catch (error) {
      console.error('Error updating regularization request:', error);
      throw error;
    }
  },

  /**
   * Delete a regularization request
   * @param {string} requestId - Request ID
   * @param {string} companyId - Company ID
   * @param {string} assigneeId - Assignee ID
   * @returns {Promise<void>}
   */
  async deleteRequest(requestId, companyId, assigneeId) {
    try {
      const requestRef = getRegularizationRequestRef(companyId, requestId);
      const assigneeQueueRef = getAssigneeQueueRef(companyId, assigneeId);
      const updatedAt = Timestamp.now();

      await database.runTransaction(async (transaction) => {
        await transaction.delete(requestRef);
        await transaction.set(
          assigneeQueueRef,
          {
            regularizationRequests: FieldValue.arrayRemove(requestId),
            updatedAt,
          },
          { merge: true }
        );
      });
    } catch (error) {
      console.error('Error deleting regularization request:', error);
      throw error;
    }
  },

  /**
   * Approve a regularization request
   * Creates a session in the sessions collection and updates request status
   * @param {Object} requestData - Request data
   * @param {string} companyId - Company ID
   * @returns {Promise<void>}
   */
  async approveRequest(requestData, companyId) {
    try {
      const {
        id: requestId,
        userId,
        teamId,
        taskHours,
        taskMinutes,
        projectId,
        taskID,
        taskName,
        taskDetails,
        idleMinutes = 0,
      } = requestData;

      const userTimezone = 'Asia/Kolkata';
      const startTimeTs = toFirestoreTimestampField(requestData.startTime);
      const endTimeTs = toFirestoreTimestampField(requestData.endTime);
      if (!startTimeTs || !endTimeTs) {
        throw new Error(
          'Invalid request data: missing startTime/endTime timestamp'
        );
      }

      const durationSeconds = (taskHours || 0) * 3600 + (taskMinutes || 0) * 60;
      const idleDurationSeconds = (idleMinutes || 0) * 60;

      const sessionData = {
        startTime: startTimeTs,
        endTime: endTimeTs,
        lastPingAt: endTimeTs,
        duration: durationSeconds,
        idleDuration: idleDurationSeconds,
        appUsage: {},
        screenshotCount: 0,
        status: 'completed',
        clientTimeZone: userTimezone,
        typeLog: 'Manual',
        source: 'regularization',
        projectId: projectId ?? null,
        taskId: taskID ?? null,
        taskName: taskName || '',
        taskDetails: taskDetails || '',
      };

      const requestRef = getRegularizationRequestRef(companyId, requestId);

      const sessionRef = database
        .collection('teamDatabase')
        .doc(teamId)
        .collection('members')
        .doc(userId)
        .collection('sessions')
        .doc();

      await database.runTransaction(async (transaction) => {
        await transaction.update(requestRef, {
          status: 'Approved',
          updatedAt: Timestamp.now(),
        });
        await transaction.set(sessionRef, sessionData);
      });

      return {
        sessionId: sessionRef.id,
        requestId,
      };
    } catch (error) {
      console.error('Error approving regularization request:', error);
      throw error;
    }
  },

  /**
   * Decline a regularization request
   * @param {string} requestId - Request ID
   * @param {string} companyId - Company ID
   * @param {string} assigneeId - Assignee ID
   * @returns {Promise<void>}
   */
  async declineRequest(requestId, companyId, assigneeId) {
    try {
      const requestRef = getRegularizationRequestRef(companyId, requestId);
      const assigneeQueueRef = getAssigneeQueueRef(companyId, assigneeId);
      const updatedAt = Timestamp.now();

      await database.runTransaction(async (transaction) => {
        await transaction.update(requestRef, {
          status: 'Declined',
          updatedAt,
        });
        await transaction.set(
          assigneeQueueRef,
          {
            regularizationRequests: FieldValue.arrayRemove(requestId),
            updatedAt,
          },
          { merge: true }
        );
      });
    } catch (error) {
      console.error('Error declining regularization request:', error);
      throw error;
    }
  },

  /**
   * Check if regularization time overlaps with existing sessions
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @param {moment.Moment} startTime - Start time (moment object)
   * @param {moment.Moment} endTime - End time (moment object)
   * @returns {Promise<Object>} Overlap information
   */
  async checkSessionOverlap(teamId, userId, startTime, endTime) {
    try {
      // Convert to UTC timestamps for query
      const startUTC = firebase.firestore.Timestamp.fromDate(
        startTime.utc().toDate()
      );
      const endUTC = firebase.firestore.Timestamp.fromDate(
        endTime.utc().toDate()
      );

      // Get day boundaries for query
      const dayStart = moment(startTime).startOf('day');
      const dayEnd = moment(startTime).endOf('day');
      const dayStartUTC = firebase.firestore.Timestamp.fromDate(
        dayStart.utc().toDate()
      );
      const dayEndUTC = firebase.firestore.Timestamp.fromDate(
        dayEnd.utc().toDate()
      );

      // Query sessions that start on this day
      const sessionsQuery1 = await database
        .collection('teamDatabase')
        .doc(teamId)
        .collection('members')
        .doc(userId)
        .collection('sessions')
        .where('startTime', '>=', dayStartUTC)
        .where('startTime', '<=', dayEndUTC)
        .get();

      // Query sessions that end on this day
      const sessionsQuery2 = await database
        .collection('teamDatabase')
        .doc(teamId)
        .collection('members')
        .doc(userId)
        .collection('sessions')
        .where('endTime', '>=', dayStartUTC)
        .where('endTime', '<=', dayEndUTC)
        .get();

      // Combine and deduplicate
      const allSessionIds = new Set();
      const allSessions = [];

      sessionsQuery1.docs.forEach((doc) => {
        if (!allSessionIds.has(doc.id)) {
          allSessionIds.add(doc.id);
          allSessions.push({ id: doc.id, ...doc.data() });
        }
      });

      sessionsQuery2.docs.forEach((doc) => {
        if (!allSessionIds.has(doc.id)) {
          allSessionIds.add(doc.id);
          allSessions.push({ id: doc.id, ...doc.data() });
        }
      });

      // Check for overlaps
      const overlappingSessions = [];
      const regStartMs = startUTC.toMillis();
      const regEndMs = endUTC.toMillis();

      for (const session of allSessions) {
        if (!session.startTime || !session.endTime) continue;

        const sessionStartMs = session.startTime.toMillis();
        const sessionEndMs = session.endTime.toMillis();

        // Check if overlap exists
        if (sessionEndMs > regStartMs && sessionStartMs < regEndMs) {
          const overlapStartMs = Math.max(sessionStartMs, regStartMs);
          const overlapEndMs = Math.min(sessionEndMs, regEndMs);
          const overlapSeconds = Math.floor(
            (overlapEndMs - overlapStartMs) / 1000
          );
          const overlapMinutes = Math.floor(overlapSeconds / 60);
          const overlapHours = Math.floor(overlapMinutes / 60);
          const remainingMinutes = overlapMinutes % 60;

          overlappingSessions.push({
            sessionId: session.id,
            sessionStart: session.startTime,
            sessionEnd: session.endTime,
            sessionDuration: session.duration || 0,
            overlapSeconds,
            overlapHours,
            overlapMinutes: remainingMinutes,
            overlapStart:
              firebase.firestore.Timestamp.fromMillis(overlapStartMs),
            overlapEnd: firebase.firestore.Timestamp.fromMillis(overlapEndMs),
            sessionStartDisplay: moment(session.startTime.toDate())
              .tz('Asia/Kolkata')
              .format('YYYY-MM-DD HH:mm:ss'),
            sessionEndDisplay: moment(session.endTime.toDate())
              .tz('Asia/Kolkata')
              .format('YYYY-MM-DD HH:mm:ss'),
          });
        }
      }

      return {
        hasOverlap: overlappingSessions.length > 0,
        overlappingSessions,
        regularizationStart: startUTC,
        regularizationEnd: endUTC,
        regularizationStartDisplay: startTime.format('YYYY-MM-DD HH:mm:ss'),
        regularizationEndDisplay: endTime.format('YYYY-MM-DD HH:mm:ss'),
      };
    } catch (error) {
      console.error('Error checking session overlap:', error);
      throw error;
    }
  },

  /**
   * Check 24 hours limit for a date
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @param {string} dateId - Date ID in format "day:month:year"
   * @param {number} addingMinutes - Minutes to add
   * @param {boolean} isToday - Whether the date is today
   * @returns {Promise<Object>} Validation result
   */
  async check24HoursLimit(teamId, userId, dateId, addingMinutes, isToday) {
    try {
      const snapshot = await database
        .collection('memberData')
        .doc(teamId)
        .collection('daysData')
        .doc(userId)
        .collection('dateId')
        .doc(dateId)
        .get();

      const existingData = snapshot.data();
      const existingMinutes = snapshot.exists
        ? (existingData.totalHours || 0) * 60 + (existingData.totalMinutes || 0)
        : 0;

      const totalMinutes = existingMinutes + addingMinutes;

      if (isToday) {
        const currentMinutes = moment().hours() * 60 + moment().minutes();
        return {
          isValid: totalMinutes <= currentMinutes,
          totalMinutes,
          currentMinutes,
          message:
            totalMinutes > currentMinutes
              ? 'Logged time exceeds current time'
              : null,
        };
      } else {
        return {
          isValid: totalMinutes <= 24 * 60,
          totalMinutes,
          maxMinutes: 24 * 60,
          message:
            totalMinutes > 24 * 60
              ? 'Total time exceeds 24 hours for this date'
              : null,
        };
      }
    } catch (error) {
      console.error('Error checking 24 hours limit:', error);
      return {
        isValid: true, // Default to valid on error
        error: error.message,
      };
    }
  },
};

export default regularizationService;
